import AddItem from 'components/shared/AddItem'
import Box from 'components/shared/Box'
import Bungalow, { BungalowDataProps } from 'components/shared/Bungalow'
import Button from 'components/shared/Button'
import Info from 'components/shared/Info'
import Link from 'components/shared/Link'
import Section from 'components/shared/Section'
import Text from 'components/shared/Text'
import {
    BUNGALOW_ADULTS_MAX_LIMIT,
    BUNGALOW_ADULTS_MIN_LIMIT,
} from 'constants/limits'
import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import {
    BungalowInfo,
    removeRoom,
    setBungalows,
    setBungalowsVisible,
} from 'store/accommodation-slice'
import { useAccommodation } from 'store/accommodation-slice/hooks'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { useAutoScroll } from 'utils/hooks'

interface AssignedByTypeProps extends BungalowDataProps {
    total: number
}

interface AssignGuestsProps {
    modalOpener: () => void
    isLoading: boolean
}

const AssignGuests = ({ modalOpener, isLoading }: AssignGuestsProps) => {
    const dispatch = useAppDispatch()
    const { animatePendingScroll, scrollIndex } = useAutoScroll()

    const { guestsToAssign, bungalows, areBungalowsVisible } = useAppSelector(
        (state) => state.accommodation
    )
    const cartAccommodation = useAppSelector(
        (state) => state.cart.items.accommodation
    )
    const { removeAccommodationFromCart } = useAccommodation(
        cartAccommodation?.productId
    )
    const { total, adults, kids, infants } = guestsToAssign

    const [areKidsAlone, setAreKidsAlone] = useState(false)
    const [assignedByType, setAssignedByType] = useState<AssignedByTypeProps>({
        total: 0,
        adults: 0,
        kids: 0,
        infants: 0,
    })

    const checkIfKidsAlone = (bungalows: BungalowInfo[]) => {
        setAreKidsAlone(
            bungalows.some(
                ({ adults, kids, infants }) =>
                    adults.length === 0 &&
                    (kids.length > 0 || infants.length > 0)
            )
        )
    }

    const updateAssignedByTypes = (bungalows: BungalowInfo[]) => {
        const adults = bungalows
            .map(({ adults }) => adults.length)
            .reduce((prev, next) => prev + next)
        const kids = bungalows
            .map(({ kids }) => kids.length)
            .reduce((prev, next) => prev + next)
        const infants = bungalows
            .map(({ infants }) => infants.length)
            .reduce((prev, next) => prev + next)
        setAssignedByType({
            total: adults + kids + infants,
            adults,
            kids,
            infants,
        })
    }

    useEffect(() => {
        if (
            !cartAccommodation ||
            !cartAccommodation.detail.specialRequests ||
            !cartAccommodation.detail.specialRequests.guests
        )
            return
        const fetchedAssigned = {
            total: cartAccommodation.detail.specialRequests.guests.length,
            adults: 0,
            kids: 0,
            infants: 0,
        }
        cartAccommodation.detail.specialRequests.guests.forEach((guest) => {
            switch (guest.personType) {
                case 'adult':
                    fetchedAssigned.adults += 1
                    break
                case 'kid':
                    fetchedAssigned.kids += 1
                    break
                default:
                    fetchedAssigned.infants += 1
                    break
            }
        })
        setAssignedByType(fetchedAssigned)
    }, [])

    const handleSelectRange = (
        type: 'adults' | 'kids' | 'infants',
        bungalow: BungalowInfo
    ) => {
        // limit only for combination of adults and kids (last time ex: BUNGALOW_ADULTS_MAX_LIMIT + 1)
        const bungalowLimit = BUNGALOW_ADULTS_MAX_LIMIT

        const bungalowAdults = bungalow.adults.length
        const bungalowKids = bungalow.kids.length
        const bungalowInfants = bungalow.infants.length
        switch (type) {
            case 'adults': {
                const minAdult = BUNGALOW_ADULTS_MIN_LIMIT
                const maxAdults = BUNGALOW_ADULTS_MAX_LIMIT
                const adultsToAssign = Math.min(
                    maxAdults,
                    adults - (assignedByType.adults - bungalowAdults)
                )
                const otherCategoriesSum = bungalowKids + bungalowInfants
                let availableToAssign = bungalowLimit - otherCategoriesSum

                availableToAssign = Math.max(minAdult, availableToAssign)

                // if there is only 1 kid, allow up to 8 adults
                if (otherCategoriesSum === 1) {
                    availableToAssign = Math.max(maxAdults, availableToAssign)
                }

                return availableToAssign > adultsToAssign
                    ? adultsToAssign
                    : availableToAssign
            }
            case 'kids': {
                const kidsToAssign = kids - (assignedByType.kids - bungalowKids)
                const otherCategoriesSum =
                    bungalowAdults +
                    bungalowInfants +
                    (bungalowAdults > 0 ? 0 : 1)
                const availableToAssign = bungalowLimit - otherCategoriesSum
                return availableToAssign > kidsToAssign
                    ? kidsToAssign
                    : availableToAssign
            }
            case 'infants': {
                const infantsToAssign =
                    infants - (assignedByType.infants - bungalowInfants)
                const otherCategoriesSum =
                    bungalowAdults + bungalowKids + (bungalowAdults > 0 ? 0 : 1)
                const availableToAssign = bungalowLimit - otherCategoriesSum
                return availableToAssign > infantsToAssign
                    ? infantsToAssign
                    : availableToAssign
            }
        }
    }
    const handleDelete = async (indexToDelete: number) => {
        const updatedBungalows = bungalows.filter(
            (_, index) => index !== indexToDelete
        )
        checkIfKidsAlone(updatedBungalows)
        dispatch(setBungalows(updatedBungalows))
        updateAssignedByTypes(updatedBungalows)

        if (areBungalowsVisible) {
            dispatch(setBungalowsVisible(false))
        }

        if (
            bungalows.some(
                (bungalow) => bungalow.room?.bungalowIndex === indexToDelete
            )
        ) {
            dispatch(removeRoom(indexToDelete))
        }
        if (cartAccommodation) {
            await removeAccommodationFromCart()
        }
    }

    const updateBungalows = async (
        indexToUpdate: number,
        updatedBungalow: BungalowDataProps
    ) => {
        const newBungalows = [...bungalows]
        const { adults, infants, kids } = updatedBungalow
        newBungalows[indexToUpdate] = {
            adults: Array(adults).fill({
                name: '',
                surname: '',
            }),
            kids: Array(kids).fill({
                name: '',
                surname: '',
            }),
            infants: Array(infants).fill({
                name: '',
                surname: '',
            }),
        }

        checkIfKidsAlone(newBungalows)
        dispatch(setBungalows(newBungalows))
        updateAssignedByTypes(newBungalows)

        if (areBungalowsVisible) {
            dispatch(setBungalowsVisible(false))
        }
        if (bungalows[indexToUpdate].room) {
            dispatch(removeRoom(indexToUpdate))
        }
        if (cartAccommodation) {
            await removeAccommodationFromCart({ editPoint: 'bungalows' })
        }
    }

    const handleContinue = () => {
        dispatch(setBungalowsVisible(true))
    }

    useEffect(() => {
        animatePendingScroll('accommodation-guests-assignment')
    }, [scrollIndex])

    return (
        <Section id="accommodation-guests-assignment">
            <Box>
                <Text variant="heading2" mb={[24, 40]}>
                    <FormattedMessage
                        id="accommodation.assign_guests.section_caption"
                        defaultMessage="Przydziel gości"
                    />
                </Text>
                <Text variant="body" color="grayscale_800" mb={[8, 12]}>
                    <FormattedMessage
                        id="accommodation.assign_guests.section_description"
                        defaultMessage="_"
                    />
                </Text>
                <Link
                    variant="secondary"
                    icon={null}
                    onClick={modalOpener}
                    data-testid="accommo-button-see-bungalows"
                >
                    <FormattedMessage
                        id="accommodation.assign_guests.our_bungalows"
                        defaultMessage="Zobacz nasze bungalowy"
                    />
                </Link>
                <Box mt={[24, 16]} isFullWidth gap={[16, 24]}>
                    {bungalows.map((bungalow, index) => (
                        <Bungalow
                            index={index}
                            values={bungalow}
                            key={`bungalow-${index}`}
                            isDeleteVisible={bungalows.length > 1}
                            adultsRange={handleSelectRange('adults', bungalow)}
                            kidsRange={handleSelectRange('kids', bungalow)}
                            infantsRange={handleSelectRange(
                                'infants',
                                bungalow
                            )}
                            handleDelete={handleDelete}
                            updateBungalow={updateBungalows}
                        />
                    ))}
                </Box>
                <Box mt={[24, 16]} gap={24} isFullWidth>
                    {adults > assignedByType.adults &&
                        bungalows.length < adults && (
                            <AddItem
                                onClick={() => {
                                    dispatch(
                                        setBungalows(
                                            bungalows.concat([
                                                {
                                                    adults: [],
                                                    infants: [],
                                                    kids: [],
                                                    room: null,
                                                },
                                            ])
                                        )
                                    )
                                }}
                                testId="accommo-button-add-another"
                            >
                                <FormattedMessage
                                    id="accommodation.assign_guests.add_bungalow"
                                    defaultMessage="Dodaj kolejny bungalow"
                                />
                            </AddItem>
                        )}
                    {(assignedByType.total !== total || areKidsAlone) && (
                        <Info color="info">
                            <>
                                {assignedByType.total !== total && (
                                    <>
                                        <FormattedMessage
                                            id="accommodation.assign_guests.guests_to_assign"
                                            defaultMessage="Pozostało do zakwaterowania {assigned_guests} z {guest_in_total} gości."
                                            values={{
                                                assigned_guests:
                                                    total -
                                                    assignedByType.total,
                                                guest_in_total: total,
                                            }}
                                        />
                                        <br />
                                    </>
                                )}
                                {areKidsAlone && (
                                    <FormattedMessage
                                        id="accommodation.assign_guests.adult_requirement"
                                        defaultMessage="Do każdego bungalowu należy przydzielić przynajmniej jedną osobę dorosłą."
                                    />
                                )}
                            </>
                        </Info>
                    )}

                    <Box isFullWidth align={['stretch', 'flex-end']}>
                        <Button
                            variant="secondary"
                            isDisabled={
                                assignedByType.total !== total ||
                                areKidsAlone ||
                                isLoading
                            }
                            onClick={handleContinue}
                            width={['100%', 'initial']}
                            testId="accommo-button-assign-guests"
                        >
                            <FormattedMessage
                                id="accommodation.assign_guests.section_caption"
                                defaultMessage="Przydziel gości"
                            />
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Section>
    )
}

export default AssignGuests
